import React from "react";
import { API_BASE_URL } from "../config";
import { v4 as uuidv4 } from 'uuid';
import { PRODUCT_CATEGORIES } from "../constants/categories";
import { useNavigate } from "react-router-dom";
import { Upload, X } from "lucide-react";

const AddProductForm = () => {
  const navigate = useNavigate();
  const [productName, setProductName] = React.useState("");
  const [productDescription, setProductDescription] = React.useState("");
  const [productPrice, setProductPrice] = React.useState("");
  const [productCategory, setProductCategory] = React.useState("");
  const [productType, setProductType] = React.useState(1);
  const [productQuantity, setProductQuantity] = React.useState(1);
  const [productImages, setProductImages] = React.useState([]);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + productImages.length > 10) {
      alert("สามารถอัปโหลดได้สูงสุด 10 รูป");
      return;
    }
    setProductImages([...productImages, ...files]);
  };

  const removeImage = (index) => {
    setProductImages(productImages.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !productName ||
      !productDescription ||
      !productPrice ||
      !productCategory ||
      !productType
    ) {
      alert("กรุณากรอกข้อมูลให้ครบถ้วน");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("name", productName);
      formData.append("description", productDescription);
      formData.append("price", productPrice);
      formData.append("category", productCategory);
      formData.append("type", productType);
      formData.append(
        "quantity",
        productType === 1 ? 1 : productQuantity
      );

      productImages.forEach((image) => {
        formData.append("images", image);
      });

      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/products/add-product`,
        {
          method: "POST",
          headers: {
            Authorization: `${token}`,
          },
          body: formData,
        }
      );

      if (response.ok) {
        alert("เพิ่มสินค้าสำเร็จ!");
        navigate(-1);
      } else {
        const errorData = await response.json();
        alert(`เกิดข้อผิดพลาด: ${errorData.message}`);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("ไม่สามารถเพิ่มสินค้าได้");
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold tracking-tight text-primary mb-8">
          เพิ่มสินค้าใหม่
        </h1>

        <form
          className="space-y-8 bg-white"
          onSubmit={handleSubmit}
        >
          {/* General Info */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-medium text-primary mb-6">ข้อมูลทั่วไป</h3>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-primary mb-1">ชื่อสินค้า</label>
                <input
                  type="text"
                  placeholder="เช่น เมาส์ไร้สาย"
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-black focus:border-black sm:text-sm p-2.5 border"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-1">รายละเอียดสินค้า</label>
                <textarea
                  placeholder="อธิบายรายละเอียดสินค้าของคุณ..."
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-black focus:border-black sm:text-sm p-2.5 border"
                  rows={4}
                  value={productDescription}
                  onChange={(e) => setProductDescription(e.target.value)}
                  required
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-1">ราคา (฿)</label>
                <input
                  type="number"
                  placeholder="0.00"
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-black focus:border-black sm:text-sm p-2.5 border"
                  value={productPrice}
                  onChange={(e) => setProductPrice(e.target.value)}
                  required
                />
              </div>
            </div>
          </div>

          {/* Category & Details */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-medium text-primary mb-6">หมวดหมู่และคลังสินค้า</h3>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-primary mb-1">หมวดหมู่</label>
                <select
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-black focus:border-black sm:text-sm p-2.5 border"
                  value={productCategory}
                  onChange={(e) => setProductCategory(e.target.value)}
                  required
                >
                  <option value="">เลือกหมวดหมู่</option>
                  {PRODUCT_CATEGORIES.map((category) => (
                    <option key={uuidv4()} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2">ประเภทสินค้า</label>
                <div className="flex items-center space-x-6">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="product-type"
                      value="1"
                      className="mr-2 text-primary focus:ring-black"
                      checked={productType === 1}
                      onChange={() => setProductType(1)}
                    />
                    สินค้าชิ้นเดียว
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="product-type"
                      value="2"
                      className="mr-2 text-primary focus:ring-black"
                      checked={productType === 2}
                      onChange={() => setProductType(2)}
                    />
                    มีสต็อกสินค้า
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-1">จำนวนสินค้า</label>
                <input
                  type="number"
                  placeholder="1"
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-black focus:border-black sm:text-sm p-2.5 border disabled:bg-gray-100 disabled:text-gray-400"
                  value={productType === 1 ? 1 : productQuantity}
                  disabled={productType === 1}
                  onChange={(e) => setProductQuantity(parseInt(e.target.value, 10))}
                />
              </div>
            </div>
          </div>

          {/* Images */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-medium text-primary mb-4">รูปภาพสินค้า</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-center w-full">
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500">คลิกเพื่ออัปโหลดรูปภาพสินค้า</p>
                    <p className="text-xs text-secondary mt-1">PNG, JPG สูงสุด 10MB (สูงสุด 10 รูป)</p>
                  </div>
                  <input type="file" className="hidden" onChange={handleImageChange} multiple accept="image/*" />
                </label>
              </div>

              {productImages.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-4">
                  {productImages.map((image, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={URL.createObjectURL(image)}
                        alt={`Product ${index + 1}`}
                        className="w-full h-24 object-cover rounded-md border border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-1 right-1 p-1 bg-white rounded-full shadow-sm hover:bg-red-50 text-gray-500 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end pt-4 space-x-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-6 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              className="px-6 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black transition-colors"
            >
              เพิ่มสินค้า
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProductForm;
